import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

const cityFaqItems = [
  { label: "İstanbul", path: "/istanbul-sss", icon: "ri-building-4-line" },
  { label: "Kapadokya", path: "/kapadokya-sss", icon: "ri-landscape-line" },
  { label: "Antalya", path: "/antalya-sss", icon: "ri-sun-line" },
  { label: "İzmir", path: "/izmir-sss", icon: "ri-ship-line" },
  { label: "Mardin", path: "/mardin-sss", icon: "ri-ancient-gate-line" },
  { label: "Trabzon", path: "/trabzon-sss", icon: "ri-tree-line" },
  { label: "Sakarya", path: "/sakarya-sss", icon: "ri-water-flash-line" },
  { label: "Kocaeli", path: "/kocaeli-sss", icon: "ri-building-line" },
  { label: "Bolu", path: "/bolu-sss", icon: "ri-plant-line" },
  { label: "Gaziantep", path: "/gaziantep-sss", icon: "ri-ancient-gate-line" },
];

const kesfetItems = [
  { label: "Gezilecek Yerler", path: "/places", icon: "ri-map-pin-2-line", desc: "Türkiye'nin en güzel noktaları" },
  { label: "Restoranlar", path: "/restoranlar", icon: "ri-restaurant-line", desc: "Editör seçimi lezzet durakları" },
  { label: "Konaklama", path: "/accommodations", icon: "ri-hotel-line", desc: "Otel, pansiyon ve bungalovlar" },
  { label: "Makaleler", path: "/articles", icon: "ri-article-line", desc: "Seyahat yazıları ve ipuçları" },
  { label: "Duyurular", path: "/duyurular", icon: "ri-megaphone-line", desc: "Haberler ve blog yazıları" },
  { label: "Karadeniz Rehberi", path: "/guides/karadeniz-yayla-rehberi", icon: "ri-water-flash-line", desc: "Yayla ve doğa rehberi" },
  { label: "Günübirlik Kaçamaklar", path: "/guides/gunubirlik-kacamaklar", icon: "ri-car-line", desc: "Kısa tatil rotaları" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [kesfetOpen, setKesfetOpen] = useState(false);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [pendingAccCount, setPendingAccCount] = useState(0);
  const location = useLocation();
  const kesfetRef = useRef<HTMLDivElement>(null);
  const cityRef = useRef<HTMLDivElement>(null);
  const kesfetTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cityTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (kesfetRef.current && !kesfetRef.current.contains(e.target as Node)) setKesfetOpen(false);
      if (cityRef.current && !cityRef.current.contains(e.target as Node)) setCityDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const checkPending = () => {
      try {
        const apps = JSON.parse(localStorage.getItem("pendingAccommodations") || "[]");
        const count = apps.filter((a: { status: string }) => a.status === "pending").length;
        setPendingAccCount(count);
      } catch { setPendingAccCount(0); }
    };
    checkPending();
    const interval = setInterval(checkPending, 5000);
    return () => clearInterval(interval);
  }, []);

  const isHome = location.pathname === "/";
  const baseText = isHome && !scrolled ? "text-white" : "text-stone-800";
  const logoText = isHome && !scrolled ? "text-white" : "text-stone-900";
  const bgClass = scrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-transparent";

  const makeDropdownHandlers = (
    setOpen: (v: boolean) => void,
    timeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>
  ) => ({
    onMouseEnter: () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setOpen(true);
    },
    onMouseLeave: () => {
      timeoutRef.current = setTimeout(() => setOpen(false), 150);
    },
  });

  const kesfetHandlers = makeDropdownHandlers(setKesfetOpen, kesfetTimeout);
  const cityHandlers = makeDropdownHandlers(setCityDropdownOpen, cityTimeout);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bgClass}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 cursor-pointer flex-shrink-0">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className={`ri-map-pin-2-fill text-xl ${isHome && !scrolled ? "text-amber-400" : "text-amber-500"}`}></i>
          </div>
          <span
            className={`font-bold text-lg tracking-tight ${logoText} transition-colors duration-300`}
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Yerel Bilgi
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1">
          {/* Ana Sayfa */}
          <Link
            to="/"
            className={`text-sm font-medium px-3 py-2 rounded-lg transition-colors duration-200 hover:text-amber-500 whitespace-nowrap cursor-pointer
              ${location.pathname === "/" ? "text-amber-500" : baseText}`}
          >
            Ana Sayfa
          </Link>

          {/* Keşfet Dropdown */}
          <div ref={kesfetRef} className="relative" {...kesfetHandlers}>
            <button
              className={`flex items-center gap-1 text-sm font-medium px-3 py-2 rounded-lg transition-colors duration-200 hover:text-amber-500 whitespace-nowrap cursor-pointer ${
                kesfetOpen ? "text-amber-500" : baseText
              }`}
            >
              Keşfet
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${kesfetOpen ? "rotate-180" : ""}`}></i>
            </button>

            <div
              className={`absolute top-full left-0 mt-3 w-72 bg-white rounded-2xl border border-stone-100 overflow-hidden transition-all duration-200 z-50 ${
                kesfetOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-2 pointer-events-none"
              }`}
              style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.10)" }}
            >
              <div className="px-4 py-3 border-b border-stone-50 bg-stone-50">
                <p className="text-xs font-bold text-stone-400 uppercase tracking-wider">İçerikler & Rehberler</p>
              </div>
              <div className="py-2">
                {kesfetItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setKesfetOpen(false)}
                    className={`flex items-start gap-3 px-4 py-2.5 hover:bg-amber-50 transition-colors cursor-pointer group ${
                      location.pathname === item.path ? "bg-amber-50" : ""
                    }`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-stone-100 group-hover:bg-amber-100 rounded-lg flex-shrink-0 mt-0.5 transition-colors">
                      <i className={`${item.icon} text-stone-500 group-hover:text-amber-600 text-sm transition-colors`}></i>
                    </div>
                    <div>
                      <p className={`text-sm font-semibold transition-colors ${location.pathname === item.path ? "text-amber-600" : "text-stone-800 group-hover:text-amber-700"}`}>
                        {item.label}
                      </p>
                      <p className="text-xs text-stone-400 mt-0.5">{item.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Şehir Rehberleri Dropdown */}
          <div ref={cityRef} className="relative" {...cityHandlers}>
            <button
              className={`flex items-center gap-1 text-sm font-medium px-3 py-2 rounded-lg transition-colors duration-200 hover:text-amber-500 whitespace-nowrap cursor-pointer ${
                cityDropdownOpen ? "text-amber-500" : baseText
              }`}
            >
              Şehir Rehberleri
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${cityDropdownOpen ? "rotate-180" : ""}`}></i>
            </button>

            <div
              className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 bg-white rounded-2xl border border-stone-100 overflow-hidden transition-all duration-200 z-50 ${
                cityDropdownOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-2 pointer-events-none"
              }`}
              style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.10)" }}
            >
              <div className="px-4 py-3 border-b border-stone-50 bg-stone-50">
                <p className="text-xs font-bold text-stone-400 uppercase tracking-wider">SSS Rehberleri</p>
              </div>
              <div className="py-2">
                {cityFaqItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setCityDropdownOpen(false)}
                    className={`flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-amber-50 hover:text-amber-700 transition-colors cursor-pointer ${
                      location.pathname === item.path ? "bg-amber-50 text-amber-700 font-semibold" : "text-stone-700"
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${item.icon} text-amber-500 text-sm`}></i>
                    </div>
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="hidden md:flex items-center gap-2">
          <Link
            to="/gold-apply"
            className="flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full bg-gradient-to-r from-amber-500 to-yellow-400 text-white hover:from-amber-600 hover:to-yellow-500 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-vip-crown-fill text-sm"></i>
            Gold Üyelik
          </Link>
          <Link
            to="/hotel-apply"
            className={`flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border transition-colors duration-200 whitespace-nowrap cursor-pointer ${
              isHome && !scrolled
                ? "border-white/40 text-white hover:bg-white/15"
                : "border-stone-300 text-stone-700 hover:bg-stone-100"
            }`}
          >
            <i className="ri-add-line text-sm"></i>
            İşletme Ekle
          </Link>

          {/* Admin bell */}
          {pendingAccCount > 0 ? (
            <Link
              to="/admin"
              className="relative flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-rose-500 hover:bg-rose-600 text-white transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-notification-3-fill text-sm"></i>
              <span>{pendingAccCount} Bekleyen</span>
            </Link>
          ) : (
            <Link
              to="/admin"
              className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors duration-200 cursor-pointer ${
                isHome && !scrolled
                  ? "text-white/60 hover:text-white hover:bg-white/10"
                  : "text-stone-400 hover:text-stone-600 hover:bg-stone-100"
              }`}
              title="Admin Paneli"
            >
              <i className="ri-shield-keyhole-line text-base"></i>
            </Link>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${baseText}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-3-line"}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-stone-100 px-4 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          <Link to="/" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 text-sm font-medium py-2.5 px-3 rounded-xl text-stone-700 hover:bg-amber-50 hover:text-amber-700 cursor-pointer transition-colors">
            <i className="ri-home-line text-amber-500"></i> Ana Sayfa
          </Link>

          <div className="border-t border-stone-100 pt-3 mt-1">
            <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2 px-3">Keşfet</p>
            {kesfetItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-3 text-sm py-2.5 px-3 rounded-xl cursor-pointer transition-colors hover:bg-amber-50 hover:text-amber-700 ${
                  location.pathname === item.path ? "text-amber-600 font-semibold bg-amber-50" : "text-stone-700"
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${item.icon} text-amber-500 text-sm`}></i>
                </div>
                {item.label}
              </Link>
            ))}
          </div>

          <div className="border-t border-stone-100 pt-3 mt-1">
            <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2 px-3">Şehir Rehberleri</p>
            <div className="grid grid-cols-2 gap-1">
              {cityFaqItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-center gap-2 text-sm py-2 px-3 rounded-xl cursor-pointer transition-colors hover:bg-amber-50 hover:text-amber-600 ${
                    location.pathname === item.path ? "text-amber-500 font-semibold" : "text-stone-600"
                  }`}
                >
                  <i className={`${item.icon} text-amber-400 text-xs`}></i>
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="border-t border-stone-100 pt-3 mt-1 flex flex-col gap-2">
            <Link
              to="/gold-apply"
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-yellow-400 text-white text-sm font-bold px-5 py-2.5 rounded-full cursor-pointer"
            >
              <i className="ri-vip-crown-fill"></i> Gold Üyelik
            </Link>
            <Link
              to="/hotel-apply"
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-center gap-2 border border-stone-300 text-stone-700 text-sm font-semibold px-5 py-2.5 rounded-full cursor-pointer hover:bg-stone-50"
            >
              <i className="ri-add-line"></i> İşletme Ekle
            </Link>
            <Link
              to="/admin"
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-center gap-2 border border-stone-200 text-stone-500 text-sm font-medium px-5 py-2.5 rounded-full cursor-pointer hover:bg-stone-50"
            >
              <i className="ri-shield-keyhole-line"></i> Admin Paneli
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
